import { useState, useEffect } from 'react';
import { soloGetLeaderboard } from '../services/api';
import { useMetadata } from '../context/MetadataContext';
import { useGlobalError } from '../context/GlobalErrorContext';

const PAGE_SIZE = 20;

/**
 * Solo leaderboard with category/timeframe filters and paging, for
 * PuzzleLeaderboard.
 *
 * Resets to global / all time and loads the first page whenever `isOpen`
 * becomes true.
 *
 * @param {boolean} isOpen - whether the modal is open
 */
export const usePuzzleLeaderboard = (isOpen) => {
  const { getText } = useMetadata();
  const { triggerError } = useGlobalError();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('global');
  const [activeTimeframe, setActiveTimeframe] = useState('all_time');
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);

  const loadLeaderboard = async (category = activeCategory, timeframe = activeTimeframe, skipValue = 0, append = false) => {
    setIsLoading(true);
    try {
      const data = await soloGetLeaderboard(category, { 
        skip: skipValue, 
        limit: PAGE_SIZE, 
        timeframe: timeframe 
      });
      const leaders = data.leaders || [];
      
      if (append) {
        setLeaderboardData(prev => [...prev, ...leaders]);
      } else {
        setLeaderboardData(leaders);
      }
      
      // If we received fewer than 20 items, there are no more
      setHasMore(leaders.length === PAGE_SIZE);
      
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      triggerError('high', getText('errors.request_failed'));
      if (!append) {
        setLeaderboardData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const changeCategory = (category) => {
    setActiveCategory(category);
    setSkip(0);
    setLeaderboardData([]);
    loadLeaderboard(category, activeTimeframe, 0, false);
  };

  const changeTimeframe = (timeframe) => {
    setActiveTimeframe(timeframe);
    setSkip(0);
    setLeaderboardData([]);
    loadLeaderboard(activeCategory, timeframe, 0, false);
  };

  const loadMore = () => {
    if (!isLoading && hasMore) {
      const newSkip = skip + PAGE_SIZE;
      setSkip(newSkip);
      loadLeaderboard(activeCategory, activeTimeframe, newSkip, true);
    }
  };

  // Load leaderboard when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveCategory('global');
      setActiveTimeframe('all_time');
      setSkip(0);
      setLeaderboardData([]);
      loadLeaderboard('global', 'all_time', 0, false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    leaderboardData,
    isLoading,
    hasMore,
    activeCategory,
    activeTimeframe,
    changeCategory,
    changeTimeframe,
    loadMore,
  };
};

export default usePuzzleLeaderboard;
